import React, { useMemo, useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Map, 
  Lock, 
  MessageSquareHeart, 
  Music, 
  Trophy, 
  RotateCw, 
  Bookmark,
  Settings,
  Heart,
  LogOut,
  User as UserIcon,
  ShieldCheck,
  ChevronRight,
  Link,
  Copy,
  X,
  Save,
  Calendar,
  Trash2
} from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { parseISO, differenceInDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const MoreScreen: React.FC = () => {
  const { signOut, withdraw, user, coupleInfo, setCoupleInfo, linkCouple, coupleId, dDayMode } = useAppContext();
  const navigate = useNavigate();
  const [partnerCode, setPartnerCode] = useState('');
  const [showLinkInput, setShowLinkInput] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  // Settings form state
  const [editInfo, setEditInfo] = useState({
    name1: '',
    name2: '',
    anniversaryDate: '',
    firstMetDate: '',
    baseDateType: 'dated' as 'met' | 'dated'
  });

  useEffect(() => {
    if (coupleInfo) {
      setEditInfo({
        name1: coupleInfo.name1,
        name2: coupleInfo.name2,
        anniversaryDate: coupleInfo.anniversaryDate,
        firstMetDate: coupleInfo.firstMetDate,
        baseDateType: coupleInfo.baseDateType
      });
    }
  }, [coupleInfo, isSettingsOpen]);

  const handleSaveSettings = async () => {
    console.log('[MoreScreen] Save button clicked. editInfo state:', editInfo);
    try {
      console.log('[MoreScreen] Calling setCoupleInfo...');
      await setCoupleInfo(editInfo);
      console.log('[MoreScreen] setCoupleInfo returned successfully.');
      setIsSettingsOpen(false);
      alert('설정이 성공적으로 저장되었습니다.');
    } catch (error: any) {
      console.error('[MoreScreen] Save operation failed:', error);
      alert(error.message || '저장에 실패했습니다.');
    }
  };

  const handleSignOut = async () => {
    if (window.confirm('로그아웃 하시겠습니까?')) {
      await signOut();
      navigate('/auth');
    }
  };

  const handleWithdraw = async () => {
    const confirm1 = window.confirm('정말로 탈퇴하시겠습니까?\n모든 데이터(타임라인, 버킷리스트 등)가 즉시 영구 삭제되며 복구할 수 없습니다.');
    if (confirm1) {
      const confirm2 = window.confirm('마지막 확인입니다. 정말로 모든 데이터를 삭제하고 탈퇴하시겠습니까?');
      if (confirm2) {
        await withdraw();
        navigate('/auth');
      }
    }
  };

  const activeDate = useMemo(() => {
    return dDayMode === 'met' ? (coupleInfo.firstMetDate || coupleInfo.anniversaryDate) : coupleInfo.anniversaryDate;
  }, [dDayMode, coupleInfo.firstMetDate, coupleInfo.anniversaryDate]);

  const diffDays = useMemo(() => {
    try {
      const start = parseISO(activeDate);
      const today = new Date();
      return differenceInDays(today, start) + 1;
    } catch (e) {
      return 0;
    }
  }, [activeDate]);

  const handleCopyCode = () => {
    if (user?.id) {
      navigator.clipboard.writeText(user.id);
      alert('연결 코드가 복사되었습니다!');
    }
  };

  const handleLink = async () => {
    if (!partnerCode.trim()) return;
    await linkCouple(partnerCode.trim());
  };

  const menuGroups = [
    {
      title: '소통 & 계획',
      items: [
        { id: 'map', icon: Map, label: '포토 맵', color: 'bg-blue-50 text-blue-500' },
        { id: 'capsule', icon: Lock, label: '타임캡슐', color: 'bg-purple-50 text-purple-500' },
        { id: 'message', icon: MessageSquareHeart, label: '비밀 쪽지', color: 'bg-pink-50 text-pink-500' },
        { id: 'bookmark', icon: Bookmark, label: '장소 북마크', color: 'bg-yellow-50 text-yellow-500' },
      ]
    },
    {
      title: '재미 & 통계',
      items: [
        { id: 'playlist', icon: Music, label: '공유 플레이리스트', color: 'bg-indigo-50 text-indigo-500' },
        { id: 'badge', icon: Trophy, label: '업적 뱃지', color: 'bg-orange-50 text-orange-500' },
        { id: 'roulette', icon: RotateCw, label: '결정 룰렛', color: 'bg-green-50 text-green-500' },
      ]
    }
  ];

  return (
    <div className="flex flex-col gap-8 pb-24">
      <header className="flex justify-between items-center py-4">
        <h1 className="text-2xl font-bold text-charcoal">더보기</h1>
        <button 
          onClick={() => setIsSettingsOpen(true)}
          className="p-2 bg-white rounded-full shadow-sm text-gray-500 hover:text-primary transition-colors"
        >
          <Settings size={20} />
        </button>
      </header>

      {/* Couple Profile Card */}
      <div className="card flex items-center gap-4 bg-white border border-secondary shadow-sm">
        <div className="flex -space-x-3">
          <div className="w-12 h-12 rounded-full border-2 border-white overflow-hidden bg-secondary flex items-center justify-center">
            <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${coupleInfo.name1}`} alt={coupleInfo.name1} />
          </div>
          <div className="w-12 h-12 rounded-full border-2 border-white overflow-hidden bg-secondary flex items-center justify-center">
            <img src={`https://api.dicebear.com/7.x/avataaars/svg?seed=${coupleInfo.name2}`} alt={coupleInfo.name2} />
          </div>
        </div>
        <div className="flex-1 text-left">
          <h2 className="font-bold text-sm">{coupleInfo.name1} & {coupleInfo.name2}</h2>
          <p className="text-[10px] text-gray-400">
            {dDayMode === 'met' ? '만난 지' : '사랑한 지'} {diffDays}일째
          </p>
        </div>
        <Heart className="text-primary fill-primary" size={20} />
      </div>

      {/* Account Info Section */}
      <section>
        <h3 className="text-xs font-bold text-gray-400 mb-4 px-1 uppercase tracking-wider">
          나의 계정
        </h3>
        <div className="bg-white rounded-[32px] border border-secondary p-2 space-y-1">
          <div className="flex items-center gap-4 p-4">
            <div className="w-12 h-12 rounded-2xl bg-secondary flex items-center justify-center text-primary">
              <UserIcon size={24} />
            </div>
            <div className="flex-1">
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-tighter">로그인 정보</p>
              <p className="text-sm font-bold text-charcoal">{user?.email || '로그인 필요'}</p>
              {user?.user_metadata?.username && (
                <p className="text-[11px] text-primary font-bold mt-0.5 flex items-center gap-1">
                  <ShieldCheck size={12} /> @{user.user_metadata.username}
                </p>
              )}
            </div>
          </div>
          
          <div className="h-px bg-secondary mx-4" />

          {/* Couple Linking Area */}
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-bold text-charcoal">내 연결 코드</p>
                <p className="text-[10px] text-gray-400 mt-1">{user?.id?.slice(0, 13)}...</p>
              </div>
              <button 
                onClick={handleCopyCode}
                className="flex items-center gap-1 px-3 py-1.5 bg-secondary text-primary text-xs font-bold rounded-lg"
              >
                <Copy size={14} /> 복사
              </button>
            </div>

            {coupleId !== user?.id && coupleId ? (
              <div className="bg-primary/10 text-primary p-3 rounded-xl text-xs font-bold flex items-center gap-2">
                <Heart size={16} fill="currentColor" /> 상대방과 연결되었습니다!
              </div>
            ) : (
              <div>
                {!showLinkInput ? (
                  <button 
                    onClick={() => setShowLinkInput(true)}
                    className="w-full flex items-center justify-center gap-2 py-3 bg-secondary text-primary text-sm font-bold rounded-xl"
                  >
                    <Link size={16} /> 파트너 코드 입력하여 연결하기
                  </button>
                ) : (
                  <motion.div 
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    className="flex gap-2"
                  >
                    <input 
                      type="text" 
                      placeholder="파트너의 코드 붙여넣기"
                      value={partnerCode}
                      onChange={(e) => setPartnerCode(e.target.value)}
                      className="flex-1 bg-secondary border-none rounded-xl px-3 py-2 text-xs focus:ring-2 focus:ring-primary outline-none"
                    />
                    <button 
                      onClick={handleLink}
                      className="bg-primary text-white px-4 py-2 rounded-xl text-xs font-bold"
                    >
                      연결
                    </button>
                  </motion.div>
                )}
              </div>
            )}
          </div>

          <div className="h-px bg-secondary mx-4" />
          
          <button 
            onClick={handleSignOut}
            className="w-full flex items-center justify-between p-4 text-red-500 font-bold text-sm active:scale-[0.98] transition-transform"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-50 rounded-xl">
                <LogOut size={18} />
              </div>
              <span>로그아웃</span>
            </div>
            <ChevronRight size={18} className="text-red-200" />
          </button>

          <div className="h-px bg-secondary mx-4" />

          <button 
            onClick={handleWithdraw}
            className="w-full flex items-center justify-between p-4 text-gray-400 font-bold text-[11px] active:scale-[0.98] transition-transform opacity-60 hover:opacity-100"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-50 rounded-xl">
                <Trash2 size={16} />
              </div>
              <span>서비스 탈퇴하기</span>
            </div>
            <ChevronRight size={14} className="text-gray-200" />
          </button>
        </div>
      </section>

      {/* Settings Modal */}
      <AnimatePresence>
        {isSettingsOpen && (
          <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsSettingsOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
              className="relative w-full max-w-md bg-white rounded-t-[40px] sm:rounded-[40px] p-8 shadow-2xl overflow-y-auto max-h-[90vh]"
            >
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-2xl font-black text-charcoal">정보 설정</h2>
                <button onClick={() => setIsSettingsOpen(false)} className="p-2 bg-secondary/50 rounded-full text-gray-400">
                  <X size={24} />
                </button>
              </div>

              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 mb-2 ml-1 uppercase tracking-wider">나의 이름</label>
                    <input 
                      type="text" 
                      value={editInfo.name1}
                      onChange={(e) => setEditInfo({...editInfo, name1: e.target.value})}
                      className="w-full bg-secondary border-none rounded-2xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary outline-none font-bold"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 mb-2 ml-1 uppercase tracking-wider">상대방 이름</label>
                    <input 
                      type="text" 
                      value={editInfo.name2}
                      onChange={(e) => setEditInfo({...editInfo, name2: e.target.value})}
                      className="w-full bg-secondary border-none rounded-2xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary outline-none font-bold"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-black text-gray-400 mb-2 ml-1 uppercase tracking-wider">처음 만난 날</label>
                  <div className="relative">
                    <input 
                      type="date" 
                      value={editInfo.firstMetDate}
                      onChange={(e) => setEditInfo({...editInfo, firstMetDate: e.target.value})}
                      className="w-full bg-secondary border-none rounded-2xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary outline-none font-bold"
                    />
                    <Calendar className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-black text-gray-400 mb-2 ml-1 uppercase tracking-wider">사귀기 시작한 날 (기념일)</label>
                  <div className="relative">
                    <input 
                      type="date" 
                      value={editInfo.anniversaryDate}
                      onChange={(e) => setEditInfo({...editInfo, anniversaryDate: e.target.value})}
                      className="w-full bg-secondary border-none rounded-2xl px-4 py-3 text-sm focus:ring-2 focus:ring-primary outline-none font-bold"
                    />
                    <Calendar className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                  </div>
                </div>

                <div>
                  <label className="block text-[10px] font-black text-gray-400 mb-2 ml-1 uppercase tracking-wider">디데이 기준일</label>
                  <div className="flex gap-2">
                    <button 
                      onClick={() => setEditInfo({...editInfo, baseDateType: 'met'})}
                      className={`flex-1 py-3 rounded-xl text-xs font-bold transition-all ${editInfo.baseDateType === 'met' ? 'bg-primary text-white shadow-md shadow-primary/20' : 'bg-secondary text-gray-400'}`}
                    >
                      처음 만난 날 기준
                    </button>
                    <button 
                      onClick={() => setEditInfo({...editInfo, baseDateType: 'dated'})}
                      className={`flex-1 py-3 rounded-xl text-xs font-bold transition-all ${editInfo.baseDateType === 'dated' ? 'bg-primary text-white shadow-md shadow-primary/20' : 'bg-secondary text-gray-400'}`}
                    >
                      기념일 기준
                    </button>
                  </div>
                </div>

                <button 
                  onClick={handleSaveSettings}
                  className="w-full bg-primary text-white font-black py-4 rounded-2xl shadow-xl shadow-primary/30 active:scale-[0.98] transition-all flex items-center justify-center gap-2 text-sm"
                >
                  <Save size={18} />
                  <span>설정 저장하기</span>
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Menu Groups */}
      {menuGroups.map((group, groupIdx) => (
        <section key={groupIdx}>
          <h3 className="text-xs font-bold text-gray-400 mb-4 px-1 uppercase tracking-wider">
            {group.title}
          </h3>
          <div className="grid grid-cols-2 gap-3">
            {group.items.map((item, itemIdx) => (
              <motion.button
                key={item.id}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: (groupIdx * 4 + itemIdx) * 0.05 }}
                className="card flex flex-col items-center justify-center gap-3 py-6 hover:border-primary/30 transition-colors bg-white border border-secondary"
              >
                <div className={`p-3 rounded-2xl ${item.color}`}>
                  <item.icon size={24} />
                </div>
                <span className="text-sm font-bold text-charcoal">{item.label}</span>
              </motion.button>
            ))}
          </div>
        </section>
      ))}

      <footer className="text-center py-4">
        <p className="text-[10px] text-gray-300">Our Moment v1.0.0</p>
      </footer>
    </div>
  );
};

export default MoreScreen;
